import { cloneElement, type JSX } from 'react';

export interface PromptMarkerRenderProps {
  marker: string;
  occurrence: number;
}

export interface PromptBodyProps {
  text: string;
  renderMarker?: (props: PromptMarkerRenderProps) => JSX.Element;
}

/**
 * The prompt as she will paste it, rendered the way the mockup draws it:
 * `##` section titles set apart by weight and letter-spacing, `[MARKERS]`
 * highlighted so the fields she has to fill are findable at a glance,
 * everything else monospace. The mockup tinted the titles with the accent, but
 * the accent does not clear WCAG AA on this surface — see the note below.
 *
 * The text is never rewritten — what is on screen is byte-for-byte what the
 * copy button puts on the clipboard.
 *
 * Monospace prose is wide by nature, so a line that will not wrap scrolls
 * inside this box. `min-width: 0` is what makes that true: without it the box
 * refuses to shrink below its content inside a grid or flex parent, and the
 * page scrolls sideways instead.
 */
export function PromptBody({ text, renderMarker }: PromptBodyProps): JSX.Element {
  let occurrence = 0;
  const nextOccurrence = (): number => occurrence++;

  return (
    <div
      style={{
        margin: 0,
        font: '400 12.5px/1.8 var(--rvk-font-mono)',
        color: 'var(--rvk-text-primary)',
        background: 'var(--rvk-surface-2)',
        border: '1px solid var(--rvk-border-subtle)',
        borderRadius: 'var(--rvk-radius-md)',
        padding: 'clamp(13px, 3vw, 20px)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        maxWidth: '100%',
        whiteSpace: 'pre-wrap',
        overflowX: 'auto'
      }}
    >
      {text.split('\n').map((line, index) => (
        <Line
          key={index}
          line={line}
          renderMarker={renderMarker}
          nextOccurrence={nextOccurrence}
        />
      ))}
    </div>
  );
}

function Line({
  line,
  renderMarker,
  nextOccurrence
}: {
  line: string;
  renderMarker: PromptBodyProps['renderMarker'];
  nextOccurrence: () => number;
}): JSX.Element {
  /* Every piece is captured, none is assumed: the hashes because a `###` from
     the model used to be redrawn as `##`, and the run of spaces because the
     flex `gap` that used to stand in for it is not a character and never
     reached the clipboard reader's eye. Both broke the byte-for-byte promise
     above for text nobody in the catalogue writes but the model does emit. */
  const heading = /^(#{2,3})([^\S\n]*)(.+)$/.exec(line);

  if (heading) {
    return (
      <span
        style={{
          display: 'flex',
          alignItems: 'baseline',
          margin: '9px 0 2px',
          minWidth: 0
        }}
      >
        {/* The hashes are prompt text, not decoration — they go to the clipboard
            with the rest, so they have to be readable. text-secondary keeps them
            quieter than the title without the opacity trick, which threw away
            whatever contrast the token guaranteed. The separator rides along
            here, where `pre-wrap` on the box preserves it. */}
        <span style={{ color: 'var(--rvk-text-secondary)', fontWeight: 600 }}>
          {heading[1]}
          {heading[2]}
        </span>
        <span
          /* accent-default on surface-2 is 1.8:1 in light commercial and 2.9:1 in
             dark personal — no accent token clears AA as bare text in all four
             brand × theme combinations. text-primary does (14.5:1 at worst);
             weight and letter-spacing carry the heading now. The markers below
             keep the accent because they pair it with a matching foreground. */
          style={{ fontWeight: 600, letterSpacing: '.05em', color: 'var(--rvk-text-primary)' }}
        >
          {withMarkers(heading[3] ?? '', renderMarker, nextOccurrence)}
        </span>
      </span>
    );
  }

  if (line.trim() === '') {
    return <span style={{ height: 7 }} />;
  }

  return <span>{withMarkers(line, renderMarker, nextOccurrence)}</span>;
}

const MARKER = /(\[[A-ZÀ-Ú0-9_ ]+\])/g;

function withMarkers(
  line: string,
  renderMarker: PromptBodyProps['renderMarker'],
  nextOccurrence: () => number
): Array<string | JSX.Element> {
  return line.split(MARKER).map((part) => {
    if (!/^\[[A-ZÀ-Ú0-9_ ]+\]$/.test(part)) return part;
    const occurrence = nextOccurrence();
    if (renderMarker) {
      return cloneElement(renderMarker({ marker: part, occurrence }), {
        key: `${part}-${occurrence}`
      });
    }
    return (
      <span
        key={`${part}-${occurrence}`}
        style={{
          padding: '1px 5px',
          borderRadius: 4,
          /* The design system's own solid-accent pair. It has to be a pair: the
             dark theme flips text-primary to cream but leaves the accent scale
             alone, so text-primary on accent-subtle read 1.5:1 in the dark. These
             two are defined together per brand and move together, which holds AA
             in all four brand × theme combinations (5.2:1 at worst, personal). */
          background: 'var(--rvk-accent-default)',
          color: 'var(--rvk-accent-fg-on-solid)',
          fontWeight: 600
        }}
      >
        {part}
      </span>
    );
  });
}
